/**
 * Calculates the time difference between the provided date and the current date,
 * and returns a human-readable string (e.g., "2 days ago").
 *
 *  Example usage:
 *    - timeAgo("2024-10-17 09:08:01.916147"); // Expected output: e.g., "1 month ago"
 *    - timeAgo("16-05-2024 20:43");           // Expected output: e.g., "6 months ago"
 *    - timeAgo("invalid-date-format");        // Expected output: "Unknown"
 *
 * @param {string} dateString - The date string to compare with the current date.
 * Accepts three formats:
 *   - "YYYY-MM-DD HH:mm:ss.ssssss" (e.g., "2024-10-17 09:08:01.916147")
 *   - "DD-MM-YYYY HH:mm" (e.g., "16-05-2024 20:43")
 *   - If an invalid date format is provided, it returns "Unknown".
 * @returns {string} A human-readable time-ago string (e.g., "3 days ago", "1 month ago"), or "Unknown" if the date is invalid.
 */

export const timeAgo = (dateString, t) => {
  let date;

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(dateString)) {
    const [datePart, timePart] = dateString.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes, seconds] = timePart.split(":").map(Number);
    date = new Date(year, month - 1, day, hours, minutes, seconds);
  } else if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}/.test(dateString)) {
    const [datePart, timePart] = dateString.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);
    const [hours, minutes] = timePart.split(":").map(Number);
    date = new Date(year, month - 1, day, hours, minutes);
  } else {
    return dateString;
  }

  if (isNaN(date.getTime())) {
    return dateString;
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  const secondsInMinute = 60;
  const secondsInHour = 3600;
  const secondsInDay = 86400;
  const secondsInMonth = 2592000; // Approx. 30 days
  const secondsInYear = 31536000; // Approx. 365 days

  if (diffInSeconds < secondsInMinute) {
    return t("prime.deviceList.justNow");
  } else if (diffInSeconds < secondsInHour) {
    const minutes = Math.floor(diffInSeconds / secondsInMinute);
    return t("prime.deviceList.minutesAgo", { minutes });
  } else if (diffInSeconds < secondsInDay) {
    const hours = Math.floor(diffInSeconds / secondsInHour);
    return t("prime.deviceList.hoursAgo", { hours });
  } else if (diffInSeconds < secondsInMonth) {
    const days = Math.floor(diffInSeconds / secondsInDay);
    return t("prime.deviceList.daysAgo", { days });
  } else if (diffInSeconds < secondsInYear) {
    const months = Math.floor(diffInSeconds / secondsInMonth);
    return t("prime.deviceList.monthsAgo", { months });
  } else {
    const years = Math.floor(diffInSeconds / secondsInYear);
    return t("prime.deviceList.yearsAgo", { years });
  }
};

/**
 * Parses a date string in one of the following formats and returns a Date object:
 *   - "YYYY-MM-DD HH:mm:ss.ssssss" (e.g., "2024-10-17 09:08:01.916147")
 *   - "DD-MM-YYYY HH:mm" (e.g., "16-05-2024 20:43")
 *
 * Example usage:
 *
 * parseDateString("2024-10-17 09:08:01.916147"); // Expected output: Date object
 *
 * parseDateString("16-05-2024 20:43");           // Expected output: Date object
 *
 * parseDateString("invalid-date-format");        // Expected output: null
 *
 * @param {string} dateString - The date string to parse.
 * @returns {Date|null} The parsed Date object, or null if the format is invalid.
 */
export const parseDateString = (dateString) => {
  let date;

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(dateString)) {
    const [datePart, timePart] = dateString.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes, seconds] = timePart.split(":").map(Number);
    date = new Date(year, month - 1, day, hours, minutes, seconds);
  } else if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}/.test(dateString)) {
    const [datePart, timePart] = dateString.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);
    const [hours, minutes] = timePart.split(":").map(Number);
    date = new Date(year, month - 1, day, hours, minutes);
  } else {
    return null;
  }

  return isNaN(date.getTime()) ? null : date;
};
